// Calls to the API on the same origin. The session cookie travels with them.

import type { BoardTimer, EncounterDetail, VitalTrend } from '@diagnuvo/domain';

export interface Me {
  id: string;
  roles: string[];
  sites: string[];
}

export class SignedOut extends Error {}
export class NotFound extends Error {}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (res.status === 401) throw new SignedOut();
  if (res.status === 404) throw new NotFound();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

export const fetchMe = () => getJson<Me>('/api/me');

export const fetchProviders = () => getJson<{ google: boolean }>('/auth/providers');

export type EncounterView = EncounterDetail & {
  siteId: string;
  encounterId: string;
  timers: BoardTimer[];
};

export const fetchEncounter = (siteId: string, encounterId: string, at?: string) =>
  getJson<EncounterView>(
    `/api/sites/${siteId}/encounters/${encounterId}${at === undefined ? '' : `?at=${encodeURIComponent(at)}`}`,
  );

/** The 15-minute vital-sign trend of one encounter, from `from` to `to` (default: now). */
export const fetchVitals = (siteId: string, encounterId: string, from: string, to?: string) =>
  getJson<VitalTrend>(
    `/api/sites/${siteId}/encounters/${encounterId}/vitals?from=${encodeURIComponent(from)}${
      to === undefined ? '' : `&to=${encodeURIComponent(to)}`
    }`,
  );

/** The alert changed since the page showed it (cleared, a new level or round). */
export class AlertChanged extends Error {}

/**
 * Acknowledges the encounter's active alert at the round and level shown (L4.2). The browser
 * sends the Origin header, which the API checks.
 */
export async function acknowledgeAlert(
  siteId: string,
  encounterId: string,
  timer: Pick<BoardTimer, 'id' | 'alert' | 'cycle'>,
): Promise<{ acknowledgedAt: string }> {
  const res = await fetch(
    `/api/sites/${siteId}/encounters/${encounterId}/alerts/${timer.id}/acknowledge`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ cycle: timer.cycle ?? 0, level: timer.alert }),
    },
  );
  if (res.status === 401) throw new SignedOut();
  if (res.status === 409) throw new AlertChanged();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as { acknowledgedAt: string };
}

export const signInUrl = (provider?: 'google') =>
  `/auth/login?returnTo=${encodeURIComponent(window.location.hash ? `/${window.location.hash}` : '/')}${
    provider === undefined ? '' : `&provider=${provider}`
  }`;
